import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { i18n } from '@wms/shared';
import { api } from '../lib/api.js';

const { t } = i18n;

interface PartnerRow {
  id: string;
  code: string;
  name: string;
  status: 'ACTIVE' | 'ARCHIVED';
  defaultSlaWorkdays: number;
  allowOperatorOrders: boolean;
  _count: { skus: number };
}

export function PartnersPage() {
  const [rows, setRows] = useState<PartnerRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [includeArchived, setIncludeArchived] = useState(false);

  useEffect(() => {
    setRows(null);
    const qs = includeArchived ? '?includeArchived=true' : '';
    api<PartnerRow[]>(`/partners${qs}`)
      .then(setRows)
      .catch((err: unknown) => setError(String(err)));
  }, [includeArchived]);

  if (error) return <div className="text-sm text-red-600">{error}</div>;
  if (!rows) return <div className="text-sm text-muted-foreground">{t('common.loading')}</div>;

  return (
    <section className="space-y-4">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <h2 className="text-xl font-semibold">{t('partner.list.title')}</h2>
        <div className="flex items-center gap-3">
          <label className="flex items-center gap-1.5 text-sm">
            <input
              type="checkbox"
              checked={includeArchived}
              onChange={(e) => setIncludeArchived(e.target.checked)}
            />
            {t('common.show_archived')}
          </label>
          <Link
            to="/partners/new"
            className="rounded bg-primary text-primary-foreground px-3 py-1.5 text-sm"
          >
            {t('partner.list.create')}
          </Link>
        </div>
      </div>

      {rows.length === 0 ? (
        <div className="rounded border border-dashed border-border p-6 text-center text-sm text-muted-foreground">
          {t('partner.list.empty')}
        </div>
      ) : (
        <>
          <div className="hidden md:block overflow-x-auto rounded-md border border-border">
            <table className="w-full text-sm">
              <thead className="bg-muted text-left">
                <tr>
                  <th className="px-3 py-2 font-medium">{t('partner.col.code')}</th>
                  <th className="px-3 py-2 font-medium">{t('partner.col.name')}</th>
                  <th className="px-3 py-2 font-medium">{t('partner.col.skus')}</th>
                  <th className="px-3 py-2 font-medium">{t('partner.col.sla')}</th>
                  <th className="px-3 py-2 font-medium">{t('partner.col.operator_orders')}</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr
                    key={row.id}
                    className={`border-t border-border ${row.status === 'ARCHIVED' ? 'text-muted-foreground' : ''}`}
                  >
                    <td className="px-3 py-2 font-mono text-xs">{row.code}</td>
                    <td className="px-3 py-2">
                      <Link to={`/partners/${row.id}`} className="hover:underline">
                        {row.name}
                      </Link>
                      {row.status === 'ARCHIVED' ? (
                        <span className="ml-2 text-[10px] uppercase tracking-wide rounded bg-muted px-1 py-0.5">
                          {t('common.archived')}
                        </span>
                      ) : null}
                    </td>
                    <td className="px-3 py-2 tabular-nums">{row._count.skus}</td>
                    <td className="px-3 py-2 tabular-nums text-xs">
                      {row.defaultSlaWorkdays} {t('partner.col.sla_unit')}
                    </td>
                    <td className="px-3 py-2 text-xs">
                      {row.allowOperatorOrders ? '✓' : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <ul className="md:hidden space-y-2">
            {rows.map((row) => (
              <li key={row.id} className="rounded-md border border-border p-3 space-y-2">
                <div className="flex items-start justify-between gap-2">
                  <Link to={`/partners/${row.id}`} className="font-medium hover:underline">
                    {row.name}
                  </Link>
                  <span className="font-mono text-xs text-muted-foreground">{row.code}</span>
                </div>
                <dl className="grid grid-cols-2 gap-x-3 gap-y-0.5 text-xs">
                  <dt className="text-muted-foreground">{t('partner.col.skus')}</dt>
                  <dd className="text-right tabular-nums">{row._count.skus}</dd>
                  <dt className="text-muted-foreground">{t('partner.col.sla')}</dt>
                  <dd className="text-right tabular-nums">
                    {row.defaultSlaWorkdays} {t('partner.col.sla_unit')}
                  </dd>
                  <dt className="text-muted-foreground">{t('partner.col.operator_orders')}</dt>
                  <dd className="text-right">{row.allowOperatorOrders ? '✓' : '—'}</dd>
                </dl>
              </li>
            ))}
          </ul>
        </>
      )}
    </section>
  );
}
